import { useEffect, useRef, useState } from 'react';
import { createClient, searchClients, type Client } from './clients';
import { esErrorDeRed, mensajeDeError, useSinConexion } from '@/core/red/conectividad';
import { useBundleOpcional } from '@/core/offline/BundleProvider';
import { buscarEnBundle } from '@/core/offline/bundle';

interface Props {
  organizationId: string;
  value: Client | null;
  onChange: (client: Client | null) => void;
  /** Permite crear el cliente sin abandonar el formulario (Spec §39). */
  allowCreate?: boolean;
  label?: string;
}

/**
 * Buscador de clientes con alta rápida.
 *
 * Se usa tanto en la pantalla de Clientes como, más adelante, dentro del
 * formulario de trabajo: crear un cliente NO debe hacer perder lo ya cargado,
 * por eso el alta ocurre acá dentro y devuelve el cliente ya seleccionado.
 */
export function ClientPicker({
  organizationId,
  value,
  onChange,
  allowCreate = true,
  label = 'Cliente',
}: Props) {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<Client[]>([]);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const sinServidor = useSinConexion();
  const { bundle, problema } = useBundleOpcional() ?? { bundle: null, problema: null };

  /**
   * Buscar, con servidor o sin él.
   *
   * Éste era el que rompía el alta sin señal: preguntaba siempre al servidor y
   * al fallar dejaba un `TypeError: Failed to fetch` debajo del campo, con la
   * lista vacía. Sin poder elegir cliente no hay trabajo que cargar, o sea que
   * el modo sin conexión no servía para lo único que tiene que servir.
   *
   * Ahora, sin servidor, se busca en la copia operativa —mismo criterio: por
   * nombre de cliente y por nombre de campo— y se dice de dónde salen los
   * resultados. Con servidor sigue mandando el servidor.
   */
  useEffect(() => {
    let cancelado = false;
    const timer = setTimeout(() => {
      void (async () => {
        if (sinServidor) {
          if (!cancelado) {
            setResults(bundle ? buscarEnBundle(bundle, term) : []);
            setError(null);
          }
          return;
        }
        try {
          const found = await searchClients(organizationId, term);
          if (!cancelado) {
            setResults(found);
            setError(null);
          }
        } catch (e) {
          if (cancelado) return;
          // Si lo que falló fue la red, no es un error que mostrar: es el modo
          // sin conexión empezando. Se contesta con la copia local.
          if (esErrorDeRed(e)) {
            setResults(bundle ? buscarEnBundle(bundle, term) : []);
            setError(null);
          } else {
            setError(mensajeDeError(e));
          }
        }
      })();
    }, 200);
    return () => {
      cancelado = true;
      clearTimeout(timer);
    };
  }, [organizationId, term, sinServidor, bundle]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const exactMatch = results.some((c) => c.name.toLowerCase() === term.trim().toLowerCase());
  // Crear un cliente nuevo SÍ necesita servidor: se guarda de verdad, con su
  // identificador, y otros lo van a ver. Sin señal no se ofrece, en vez de
  // ofrecerlo y fallar.
  const canCreate = allowCreate && !sinServidor && term.trim().length > 1 && !exactMatch;

  const create = async () => {
    setCreating(true);
    setError(null);
    try {
      const nuevo = await createClient({ organizationId, name: term });
      onChange(nuevo);
      setTerm('');
      setOpen(false);
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setCreating(false);
    }
  };

  if (value) {
    return (
      <div className="flex flex-col gap-1">
        <span className="text-sm">{label}</span>
        <div className="flex items-center justify-between gap-2 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 py-2">
          <span className="text-sm font-medium" data-testid="picker-selected">
            {value.name}
          </span>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="min-h-11 text-sm underline opacity-70"
            data-testid="picker-clear"
          >
            Cambiar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col gap-1" ref={boxRef}>
      <label className="text-sm" htmlFor="client-picker">
        {label}
      </label>
      <input
        id="client-picker"
        type="text"
        value={term}
        placeholder="Cliente o campo…"
        onChange={(e) => {
          setTerm(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3"
        data-testid="picker-input"
        autoComplete="off"
      />

      {error && <p className="text-sm text-[var(--peligro)]">{error}</p>}

      {/* De dónde salen los resultados, cuando no salen del servidor. Discreto
          pero presente: buscar y no encontrar a alguien que uno sabe que
          existe se explica solo si se sabe que la lista es la del teléfono. */}
      {sinServidor && (
        <p className="text-xs text-[var(--tinta-suave)]" data-testid="picker-offline">
          {bundle
            ? 'Sin conexión · mostrando datos guardados en este teléfono'
            : problema
              ? `Sin conexión y no hay clientes descargados. La última descarga no se pudo completar: ${problema}`
              : 'Sin conexión y todavía no hay clientes descargados en este dispositivo. Conectate una vez para prepararlo para uso offline.'}
        </p>
      )}

      {open && (
        <ul
          className="absolute top-full z-10 mt-1 max-h-64 w-full overflow-auto rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] bg-[var(--superficie)] shadow"
          data-testid="picker-results"
        >
          {results.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => {
                  onChange(c);
                  setOpen(false);
                  setTerm('');
                }}
                className="min-h-11 w-full px-3 py-2 text-left text-sm hover:bg-[var(--superficie-suave)]"
                data-testid="picker-option"
              >
                {c.name}
                {/* Por qué apareció. Buscar «El Trebol» y que conteste «Rombola
                    Hns.» a secas se lee como un error del buscador; con el
                    campo debajo se entiende de una. */}
                {c.matched_establishment && (
                  <span
                    className="block text-xs text-[var(--tinta-tenue)]"
                    data-testid="picker-match"
                  >
                    {c.matched_establishment}
                  </span>
                )}
              </button>
            </li>
          ))}

          {results.length === 0 && !canCreate && (
            <li className="px-3 py-2 text-sm text-[var(--tinta-suave)]">Sin resultados</li>
          )}

          {canCreate && (
            <li className="border-t border-[var(--linea)]">
              <button
                type="button"
                onClick={() => void create()}
                disabled={creating}
                className="min-h-11 w-full px-3 py-2 text-left text-sm font-medium disabled:opacity-50"
                data-testid="picker-create"
              >
                {creating ? 'Creando…' : `+ Crear «${term.trim()}»`}
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
